import React, { useEffect, useState } from "react";
import { useParams } from "react-router";
import CalendarHeatmap from "react-calendar-heatmap";
import { format, parse, getYear, addMonths, subMonths } from "date-fns";
import UpdateHabit from "../components/UpdateHabit";
import DeleteHabit from "../components/DeleteHabit";
import yearArray from "../utils/yearArray";
import useWindowDimensions from "../utils/useWindowDimensions";
import getDatesForGraph, { IDatesForGraph } from "../utils/getDatesForGraph";
import "../styles/pages/habit-graph.scss";
import arrow_left from "../styles/utils/arrow_left.svg";
import arrow_right from "../styles/utils/arrow_right.svg";
import { Tooltip } from "@mui/material";
import { privateNavigationLinks } from "../utils/navigationLinks";
import NavbarScroller from "../components/Navbar";

export type HabitGraphParams = {
  id: string;
};

type DateValues = {
  date: string | null | undefined | Date;
  count: number;
};

type HabitData = {
  habit_id: number;
  habit_name: string;
  habit_description?: string;
  dates_worked: string[];
};

const HabitGraph = () => {
  let dateValues: DateValues[] = [];
  const [dValues, setDValues] = useState<DateValues[]>([]);

  const { id } = useParams<HabitGraphParams>();
  const [habitData, setHabitData] = useState<HabitData>({
    habit_id: -999,
    habit_name: "",
    habit_description: "",
    dates_worked: [],
  });

  //setting initial state to the index of current year
  const [year, setYear] = useState(
    yearArray.indexOf(getYear(new Date()).toString())
  );
  const [month, setMonth] = useState(1);
  const [datesForGraph, setDatesForGraph] = useState<IDatesForGraph>({
    startDate: "",
    endDate: "",
  });
  const { width, height } = useWindowDimensions();

  useEffect(() => {
    (async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/habit/${id}`,
          {
            headers: { "Content-Type": "application/json" },
            credentials: "include",
          }
        );
        const data = await response.json();

        setHabitData({
          habit_id: data[0]?.habit_id,
          habit_name: data[0]?.habit_name,
          habit_description: data[0]?.habit_description,
          dates_worked: data[0].dates_worked,
        });
      } catch (error) {}
    })();
  }, []);

  useEffect(() => {
    const dates = getDatesForGraph(yearArray[year], width);
    setDatesForGraph({ startDate: dates?.startDate, endDate: dates?.endDate });
  }, [width, year]);

  useEffect(() => {
    if (habitData.dates_worked === null) {
      return;
    }
    habitData.dates_worked.map((d) => {
      setDValues((prev) => [...prev, { date: d, count: 1 }]);
    });
  }, [habitData]);

  return (
    <>
      <NavbarScroller links={privateNavigationLinks.links} />
      <div className="habit-graph">
        <h1 className="habit-name">{habitData.habit_name}</h1>
        <h2 className="description">
          {/* Habit Description ={" "} */}
          {habitData.habit_description !== "" ||
          habitData.habit_description !== null
            ? habitData.habit_description
            : "Null"}
        </h2>
        <h3 className="year">{yearArray[year]}</h3>
        <div className="edit-habit">
          <div className="update-habit">
            <UpdateHabit habitId={parseInt(id)} />
          </div>
          <div className="delete-habit">
            <DeleteHabit habitId={parseInt(id)} />
          </div>
        </div>

        <ul></ul>
        <div className="heatmap">
          <CalendarHeatmap
            startDate={datesForGraph.startDate}
            endDate={datesForGraph.endDate}
            values={dValues}
            // weekdayLabels={["Sat", "Sun", "Mon", "Tue", "Wed", "Thu", "Fri"]}
            weekdayLabels={["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]}
            showWeekdayLabels
            titleForValue={(value) => {
              if (value !== null) {
                const parsedDate = parse(value?.date, "MM/dd/yyyy", Date.now());
                // const parsedDate = parse(value?.date)

                const formattedDate = format(parsedDate, "do MMM yyyy");

                return `Habit recorded on ${formattedDate}`;
              }
              return `No habit record found on this day`;
            }}
            gutterSize={4}
          />
        </div>

        <div className="navigation-arrows">
          <Tooltip title="Previous year" placement="top">
            <img
              src={arrow_left}
              alt="previous year icon"
              onClick={() => {
                //call that getDatesForGraph Function here, set the start date and end dates based on the return value of that function

                if (year === 0) {
                  // return setYear(0);
                  return;
                }
                return setYear((year) => (year - 1) % yearArray.length);
              }}
              className="arrow-left"
            />
          </Tooltip>

          <Tooltip title="Next year" placement="top">
            <img
              src={arrow_right}
              alt="next year icon"
              onClick={() => {
                //call that getDatesForGraph Function here, set the start date and end dates based on the return value of that function

                if (year === yearArray.length - 1) {
                  return;
                }
                return setYear((year) => (year + 1) % yearArray.length);
              }}
              className="arrow-right"
            />
          </Tooltip>
        </div>
      </div>
    </>
  );
};

export default HabitGraph;

//ipad - 4 months, current inclusive
//mobile - 3 months, current inclusive
//desktop - 12 months, current inclusive

//if current year, end from current date and move back through x months from that date as per screen resolution
//if not current year, if in the future, start from 01 Jan and display x months from that date as per screen resolution
//if not current year, in the past, end from 31 Dec that year and move back through x months from that date as per screen resolution
